import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation, useParams } from 'react-router-dom'
import { useEffect, lazy, Suspense } from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from 'react-hot-toast'
import { AuthProvider } from './contexts/AuthContext'
import { LoadingState } from './components/ui'
import FloatingAIMentor from './components/FloatingAIMentor'
import ErrorBoundary from './components/ErrorBoundary'
import ProtectedRoute from './components/ProtectedRoute'
import AppLanding from './components/AppLanding'
import AdminRoute from './components/AdminRoute'
import HomeEnhanced from './pages/HomeEnhanced'
import Login from './pages/Login'
import Register from './pages/Register'
// Lazy-load heavy pages so Prism.js + Monaco editor only download when needed.
// The live/self-paced sessions embed the Monaco editor for coding questions, so
// keeping them lazy keeps Monaco out of the initial bundle.
const CodingChallengePage = lazy(() => import('./pages/CodingChallengePage'))
const Dashboard = lazy(() => import('./pages/Dashboard'))
const GitHubCallback = lazy(() => import('./pages/GitHubCallback'))
const JoinClassroom = lazy(() => import('./pages/classroom/JoinClassroom'))
const StudentDashboard = lazy(() => import('./pages/StudentDashboard'))
const StudentLearningDashboard = lazy(() => import('./pages/StudentLearningDashboard'))
const JoinLab = lazy(() => import('./pages/JoinLab'))
const LabWorkspace = lazy(() => import('./pages/LabWorkspace'))
const LabReview = lazy(() => import('./pages/LabReview'))
const QuizTaking = lazy(() => import('./pages/QuizTaking'))
const PathDetailEnhanced = lazy(() => import('./pages/PathDetailEnhanced'))
const ModuleLearningEnhanced = lazy(() => import('./pages/ModuleLearningEnhanced'))
const LearningEnhanced = lazy(() => import('./pages/LearningEnhanced'))
const AuthCallback = lazy(() => import('./pages/AuthCallback'))
const CompleteProfile = lazy(() => import('./pages/CompleteProfile'))
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'))
const JoinQuiz = lazy(() => import('./pages/JoinQuiz'))
const QuizLobby = lazy(() => import('./pages/QuizLobby'))
const QuizResults = lazy(() => import('./pages/QuizResults'))
const VideoCoursePage = lazy(() => import('./pages/VideoCoursePage'))
const LiveQuizSession = lazy(() => import('./pages/LiveQuizSession'))
const SelfPacedQuizSession = lazy(() => import('./pages/SelfPacedQuizSession'))
// Secondary / role-specific pages — kept out of the initial student bundle.
// The admin/instructor dashboards and community moderation pull in recharts.
const AdminDashboard = lazy(() => import('./pages/AdminDashboardNew'))
const InstructorDashboard = lazy(() => import('./pages/InstructorDashboard'))
const Certificates = lazy(() => import('./pages/Certificates'))
const RequestInstructorAccess = lazy(
  () => import('./components/instructor/RequestAccess'))
// Public and lazy. A notice you have to sign in to read is not a notice, and it
// is opened in a new tab from a consent checkbox rather than browsed to.
const InstructorVerificationPrivacy = lazy(
  () => import('./pages/InstructorVerificationPrivacy'))
// Public, and first in the chain: /instructor-access is protected, so a
// recipient with no account is bounced before anything can read the code out of
// the URL. See pages/InviteLanding.
const InviteLanding = lazy(() => import('./pages/InviteLanding'))
// Lazy: the career map is a browse screen most sessions never open, and it
// should not sit in the main bundle.
const CareerMap = lazy(() => import('./pages/CareerMap'))
const Leaderboard = lazy(() => import('./pages/Leaderboard'))
const QuestionManagement = lazy(() => import('./pages/QuestionManagement'))
const ProjectsEnhanced = lazy(() => import('./pages/ProjectsEnhanced'))
const ProjectDetail = lazy(() => import('./pages/ProjectDetail'))
const CommunityEnhanced = lazy(() => import('./pages/CommunityEnhanced'))
const ChatPage = lazy(() => import('./pages/ChatPage'))
const ProfileEnhanced = lazy(() => import('./pages/ProfileEnhanced'))
const UserProfileView = lazy(() => import('./pages/UserProfileView'))
const QuizAnalytics = lazy(() => import('./pages/QuizAnalytics'))
const ResumePage = lazy(() => import('./pages/ResumePage'))
const FeaturedProjects = lazy(() => import('./pages/FeaturedProjects'))
import { App as CapApp } from '@capacitor/app'
import { Browser } from '@capacitor/browser'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,        // 5 minutes - data considered fresh
      gcTime: 30 * 60 * 1000,          // 30 minutes - garbage collection
      refetchOnMount: false,            // Don't refetch if data exists and fresh
      refetchOnReconnect: false,
    },
  },
})

/**
 * Hide the AI Mentor on anything being graded.
 *
 * '/challenges/' never matched: coding challenges are at
 * /learning/challenges/:slug, so startsWith('/challenges/') was false and an AI
 * assistant sat on top of a page that blocks the clipboard and counts
 * tab-switches. /quiz/:quizId was not listed at all.
 *
 * '/lab/' was the same mistake made a second time: the coding lab shipped after
 * this list was written and nobody added it, so an AI assistant floated over a
 * proctored lab — the one surface where asking it for the answer is precisely
 * what the whole exercise prevents. bundle.test.ts now asserts against the
 * route table, so a graded route added later fails the build rather than
 * quietly re-exposing the mentor.
 *
 * Prefixes are broad on purpose — '/quiz/' covers every quiz route, so renaming
 * or adding one cannot silently re-expose the mentor mid-exam. '/lab/' hides it
 * from the instructor's monitor too; that costs an instructor nothing and means
 * there is no lab URL where the rule has to be reasoned about.
 */
function ConditionalAIMentor() {
  const { pathname } = useLocation()
  const hideOnRoutes = ['/quiz/', '/join-quiz/', '/learning/challenges/', '/lab/']
  const shouldHide = hideOnRoutes.some(r => pathname.startsWith(r))
  if (shouldHide) return null
  return <FloatingAIMentor />
}

/** Error boundary that clears itself when the user navigates elsewhere. */
function RoutedErrorBoundary({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation()
  return <ErrorBoundary resetKey={pathname}>{children}</ErrorBoundary>
}

/**
 * `/community/posts/<id>` -> `/community?post=<id>`.
 *
 * The feed listens for `?post=` and scrolls to that card. The path shape is
 * what `share_to_channel` minted into every message it has ever sent, and it
 * matched no route — so following one fell through to the catch-all and
 * redirected to the public landing page, which is indistinguishable from being
 * signed out. The sender is fixed; this keeps the messages already out there
 * working.
 */
function SharedPostRedirect() {
  const { postId } = useParams()
  const { hash } = useLocation()
  // The hash is carried through: the moderation queue links to
  // `/community/posts/<id>#comment-<id>` to point at a reported comment, and
  // dropping it would land the moderator on the post with nothing highlighted.
  return <Navigate to={`/community?post=${postId ?? ''}${hash}`} replace />
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Router>
          <div className="min-h-screen bg-slate-950 text-white">
            <AppMobileHandler />
            {/* Toast theme per DESIGN_SYSTEM.md §10 — neutral surface, icon color is the only status signal */}
            <Toaster
              position="bottom-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: '#18181b',
                  color: '#fafafa',
                  border: '1px solid rgba(63,63,70,0.6)',
                  borderRadius: '0.875rem',
                  boxShadow: '0 20px 25px -5px rgba(0,0,0,0.4), 0 8px 10px -6px rgba(0,0,0,0.4)',
                  padding: '12px 16px',
                  fontSize: '14px',
                  maxWidth: 'min(92vw, 380px)',
                },
                success: { iconTheme: { primary: '#34d399', secondary: '#18181b' } },
                error: { iconTheme: { primary: '#f87171', secondary: '#18181b' } },
              }}
            />
            <Suspense fallback={<div className="min-h-screen bg-neutral-950 flex items-center justify-center"><LoadingState /></div>}>
            <RoutedErrorBoundary>
            <Routes>
              {/* The app cold-starts here — server.url is the bare origin —
                  and the marketing page looks signed out however good the
                  session is. See components/AppLanding. */}
              <Route path="/" element={<AppLanding landing={<HomeEnhanced />} />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              {/* The app's own callback. It exists so the app can claim
                  this one URL without also claiming the website's. */}
              <Route path="/auth/app-callback" element={<AuthCallback />} />
              <Route path="/complete-profile" element={<CompleteProfile />} />
              <Route path="/verify-email/:uid/:token" element={<VerifyEmail />} />
              <Route path="/privacy/instructor-verification"
                element={<InstructorVerificationPrivacy />} />
              <Route path="/invite/:code" element={<InviteLanding />} />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin"
                element={
                  <AdminRoute>
                    <AdminDashboard />
                  </AdminRoute>
                }
              />
              <Route
                path="/learning-admin"
                element={
                  <AdminRoute allowInstructor={true}>
                    <AdminDashboard />
                  </AdminRoute>
                }
              />
              <Route
                path="/instructor"
                element={
                  <ProtectedRoute requiredRole="instructor">
                    <InstructorDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/student"
                element={
                  <ProtectedRoute requiredRole="student">
                    <StudentDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/learning"
                element={
                  <ProtectedRoute>
                    <LearningEnhanced />
                  </ProtectedRoute>
                }
              />
              {/* Its own route rather than a modal: linkable, shareable, and the
                  back button works. */}
              <Route
                path="/learning/careers"
                element={
                  <ProtectedRoute>
                    <CareerMap />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/lab/join"
                element={
                  <ProtectedRoute>
                    <JoinLab />
                  </ProtectedRoute>
                }
              />
              {/* The invite link an instructor hands out. Carries ?code= so
                  nobody has to read six characters off a projector. */}
              <Route
                path="/classroom/join"
                element={
                  <ProtectedRoute>
                    <JoinClassroom />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/lab/:labId"
                element={
                  <ProtectedRoute>
                    <LabWorkspace />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/lab/:labId/review"
                element={
                  <ProtectedRoute>
                    <LabReview />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/my-learning"
                element={
                  <ProtectedRoute>
                    <StudentLearningDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/quiz/:quizId"
                element={
                  <ProtectedRoute>
                    <QuizTaking />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/quiz/:quizId/questions"
                element={
                  <ProtectedRoute>
                    <QuestionManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/learning/paths/:pathId"
                element={
                  <ProtectedRoute>
                    <PathDetailEnhanced />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/learning/modules/:moduleId"
                element={
                  <ProtectedRoute>
                    <ModuleLearningEnhanced />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/learning/challenges/:slug"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<div className="min-h-screen bg-neutral-950 flex items-center justify-center"><LoadingState /></div>}>
                      <CodingChallengePage />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/learning/videos/:slug"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<div className="min-h-screen bg-neutral-950 flex items-center justify-center"><LoadingState /></div>}>
                      <VideoCoursePage />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/projects"
                element={
                  <ProtectedRoute>
                    <ProjectsEnhanced />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/projects/explore"
                element={
                  <ProtectedRoute>
                    <FeaturedProjects />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/projects/:slug"
                element={
                  <ProtectedRoute>
                    <ProjectDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/community"
                element={
                  <ProtectedRoute>
                    <CommunityEnhanced />
                  </ProtectedRoute>
                }
              />
              {/* Messages already sent to channels carry this shape, and it
                  matched nothing — so following one hit the catch-all below and
                  landed on the public page, which reads as being signed out.
                  Kept as a redirect so those links keep working. */}
              {/* Where GitHub returns after authorising. Must match the
                  callback URL registered on the OAuth app exactly. */}
              <Route
                path="/projects/github/callback"
                element={
                  <ProtectedRoute>
                    <GitHubCallback />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/community/posts/:postId"
                element={<SharedPostRedirect />}
              />
              <Route
                path="/chat"
                element={
                  <ProtectedRoute>
                    <ChatPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <ProfileEnhanced />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/resume"
                element={
                  <ProtectedRoute>
                    <ResumePage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/user/:userId"
                element={
                  <ProtectedRoute>
                    <UserProfileView />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/certificates"
                element={
                  <ProtectedRoute>
                    <Certificates />
                  </ProtectedRoute>
                }
              />
              {/* Asking for instructor access. Its own route rather than a step
                  in the sign-up wizard: self-registration must never grant an
                  elevated role, so this runs against an account that already
                  exists — and the wizard stays three steps for the students who
                  are almost everybody. */}
              <Route
                path="/instructor-access"
                element={
                  <ProtectedRoute>
                    <div className="min-h-screen bg-neutral-950">
                      <main className="mx-auto max-w-3xl px-4 py-6 sm:px-6 sm:py-8">
                        <RequestInstructorAccess
                          onGranted={() => window.location.assign('/instructor')}
                        />
                      </main>
                    </div>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/leaderboard"
                element={
                  <ProtectedRoute>
                    <Leaderboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/join"
                element={
                  <ProtectedRoute>
                    <JoinQuiz />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/join-quiz/:code"
                element={
                  <ProtectedRoute>
                    <JoinQuiz />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/quiz/lobby/:joinCode"
                element={
                  <ProtectedRoute>
                    <QuizLobby />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/quiz/live/:joinCode"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<div className="min-h-screen bg-neutral-950 flex items-center justify-center"><LoadingState /></div>}>
                      <LiveQuizSession />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/quiz/self-paced/:joinCode"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<div className="min-h-screen bg-neutral-950 flex items-center justify-center"><LoadingState /></div>}>
                      <SelfPacedQuizSession />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/quiz/results"
                element={
                  <ProtectedRoute>
                    <QuizResults />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/quiz/analytics/:quizId"
                element={
                  <ProtectedRoute>
                    <QuizAnalytics />
                  </ProtectedRoute>
                }
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            </RoutedErrorBoundary>
            </Suspense>
            <ConditionalAIMentor />
          </div>
        </Router>
      </AuthProvider>
    </QueryClientProvider>
  )
}

function AppMobileHandler() {
  const navigate = useNavigate()

  useEffect(() => {
    /**
     * Coming back from a sign-in that happened outside the app.
     *
     * Google refuses OAuth inside an embedded WebView, so the app opens it in
     * a Chrome Custom Tab and Google redirects to
     * https://ccis-codehub.space/auth/callback. Android hands that URL back
     * here as a deep link — but only once it has verified
     * /.well-known/assetlinks.json, which was being answered by the SPA
     * fallback with index.html. Every sign-in therefore finished on the
     * website in a browser instead of in the app.
     */
    CapApp.addListener('appUrlOpen', (data: { url: string }) => {
      // The tab that Google was shown in stays open behind the app otherwise:
      // you land back on the site the moment anything closes the app view.
      void Browser.close().catch(() => {})

      let target: string
      try {
        // Parsed rather than split on '.space' — that matched anywhere in the
        // URL, and a code or returnUrl containing it took the wrong half.
        const url = new URL(data.url)
        target = `${url.pathname}${url.search}${url.hash}`
      } catch {
        return
      }
      if (target && target !== '/') navigate(target)
    })

    /*
     * The redirect that used to live here — app + pathname '/' → '/login' —
     * is gone.
     *
     * It never looked at whether anyone was signed in, and the app cold-starts
     * on '/' every time because server.url is the bare origin. So every launch
     * ended on the login screen no matter how healthy the session was, and it
     * ran from an effect, which meant it also silently overrode the route's own
     * decision about where a signed-in user belongs.
     *
     * Its intent was right — the marketing page is the wrong front door for the
     * app — and now lives in components/AppLanding, which makes the same choice
     * with the session in hand.
     */

    return () => {
      CapApp.removeAllListeners()
    }
  }, [navigate])

  return null
}

export default App
